import React, { useState, useEffect, useRef  } from "react";
import { useDispatch, useSelector } from "react-redux";
import PopUp from "../ReactPortal/PortalPopUp";
import styles from "../OliPopUp/OliPopUp.module.scss";
import Srcset from "@/components/common/Srcset";
import { useRouter } from "next/compat/router";
import { getCookie, getCookies, setCookie } from "cookies-next";
import { checkIfProductExists } from "@/helpers/algoliaSearch";
import { handlePopupVisibility } from "@/helpers/popUpHelper";
import { analyzeUserPrompt, getOliStatus, analyzePromptFeedback } from "@/helpers/oliHelper";
import {validateUserInput} from "@/helpers/userInputHelper";
import { closeOliPopup, openOliPopup, displayFeedbackPopup } from "@/redux/actions/oliCategoryAction";
import Oli_Icon from '@/components/Logos/OliAI';
import useMediaQuery from "@/helpers/hooks/useMediaQuery";
import OliPopUpMini from "./OliPopUpMini";

export default function OliPopUp() {
  const [oliToggleStatus, setOliToggleStatus] = useState(false);
  const [searchText, setSearchText] = useState(""); // track textarea input
  const [noResults, setNoResults] = useState(false); // track no results flag
  const router = useRouter();
  const isPDP = router.asPath.includes("/products/");
  const logUser = useSelector((state)=> state.loginUser);
  var userId = "";
  // Get the first name of the logged-in user
  const firstName = logUser?.userData?.firstName || "";
  if(logUser?.userData?.id){
    userId = atob(logUser?.userData?.id).split('/').pop();
  }
  const isPWA = useSelector((state) => state.pwa.is_PWA);
  const [loading, setLoading] = useState(false); //track search loading state
  const isHomePage = router.pathname === "/";
  const isDesigner = router.asPath.includes("/designer");
  const inputMaxLength = 255; // Set your desired max length here
  const [errorMessage, setErrorMessage] = useState("");
  const textareaRef = useRef(null); // Create a ref for the textarea
  const [isFocused, setIsFocused] = useState(false);
  const popUpRef = useRef(null);
  const [isError, setIsError] = useState(false); //Error state to be used for API errors or other errors
  const [nonClothingError, setNonClothingError] = useState(null); // Error state for non-clothing items
  // pop up visibility state from redux
  const dispatch = useDispatch();
  const isVisible = useSelector((state) => state.oliCategory.isVisible);
  // states defined for the feedback popup
  const showFeedbackPopup = useSelector((state) => state.oliCategory.showFeedbackPopup);
  const [hasAnimated, setHasAnimated] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isOliPLP = router.asPath.includes("search_source=oli_plp");
  const [selectedFeedback, setSelectedFeedback] = useState(null); // State to track the selected feedback type
  const isSubmenuVisible = useSelector((state) => state.header.isSubmenuVisible); //redux state to track if submenu is visible
  const GA4_MEASUREMENT_ID = (process.env.APP_MODE == 'production') ? "_ga_7YBDDD5RP7": "_ga_EZQ6LM9EQB";
  const ga_session_id = getCookie(GA4_MEASUREMENT_ID)?.split(".")[2];

  const isPLP = (() => {
    try{
      const pageTypeCookie = getCookie("currentPageType");
      if(pageTypeCookie){
        const parsedCookie = JSON.parse(pageTypeCookie);
        return parsedCookie?.type === "collection";
      }
    }catch (error){
      reportError("Error in parsing pageTypeCookie", error);
    }
    return false;
  })();
  
  // to handle the close button click
  const popupCloseHandler = (e) => {
    setSearchText('');
    setIsFocused(false)
    setErrorMessage("");
    setNoResults(false);
    dispatch(closeOliPopup());
    setNonClothingError(false);
  };

  // Function to perform the Olivela search
  const performOliSearch = async () => {
    setIsError(false);
    setNoResults(false);
    setNonClothingError(false);
    setLoading(true);
    // Fetch oli status, and hit the API only if the oli is turned on
    const status = await fetchOliStatus(false) 
    if(status) {
      let oliResponse;
      if(searchText){
        oliResponse = await analyzeUserPrompt(searchText?.trim(), userId, ga_session_id);
        if((oliResponse && !oliResponse?.error) || searchText.includes('clothing') || searchText.includes('cloths')) {
          if(oliResponse !== 'no-result' || searchText.includes('clothing') || searchText.includes('cloths')) {
            let result = await checkIfProductExists(`${process.env.NETLIFY_URL}/women/clothing?${oliResponse}`)
            if(result) {
              // setNoResults(false);
              // setLoading(false)
              // Store the search text in session storage
              sessionStorage.setItem("searchText", searchText);
              // Set the feedback pop-up display flag in the session storage
              if(!sessionStorage.getItem("showFeedbackPopup")){
                sessionStorage.setItem("showFeedbackPopup", "true");
              }
              // Redirect the user to a static URL
              window.location.href = `${process.env.NETLIFY_URL}/women/clothing?search_source=oli_plp&${oliResponse}`;
            } else {
              setNoResults(true);
              setLoading(false)
            }
          } else {
            setNoResults(true);
            setLoading(false)
          }
        } else {
          setIsError(true);
          setLoading(false)
          if(oliResponse && oliResponse?.error && (oliResponse?.jsonObject?.is_clothing === false || oliResponse?.jsonObject?.Clothing?.is_clothing === false)){
            setNonClothingError("I'm still learning. I can only assist with clothing at the moment, but you can explore all categories through our navigation.");
          }
        }
      }
    } else {
      setIsError(true);
      setLoading(false)
    }
  };

  // fetch oli status
  // onload is true by default to fetch oli status on page load
  const fetchOliStatus = async (onload=true) => {
    // set isError to false to clear error messages
    setIsError(false)
    // fetch api from shopify to get the status of oli toggle
    const status = await getOliStatus();

    if(onload) {
      if(status){
        setOliToggleStatus(true);
      }
      else {
        setOliToggleStatus(false);
      }
    } else {
      // if toggle is turned off, show error message
      if(!status) {
        setIsError(true);
      }
      return status
    }
  };

  useEffect(() => {
    // fetch oli toggle status on page load
    fetchOliStatus();
  }, []);

  useEffect(() => {
    // Check if the URL contains the parameter `oli_trigger=true` and is the homepage
    const urlParams = new URLSearchParams(window.location.search);
    const oliTrigger = urlParams.get("oli_trigger");

    if (router.pathname === "/" && oliTrigger === "true") {
      dispatch(openOliPopup()); // Dispatch the Redux action
    }
  }, [router.pathname, dispatch]); // Re-run the effect if the pathname changes

  const handleTextareaChange = (e) => {
    const value = e.target.value;
    const { isValid, errorMessage, sanitizedValue } = validateUserInput(value, inputMaxLength);

    if (!isValid) {
        setErrorMessage(errorMessage);
        setSearchText(sanitizedValue); // Update the search text with the sanitized value
        return; // Exit early
    }

    // If all checks pass, update the search text
    setSearchText(sanitizedValue);
    setErrorMessage(''); // Clear any previous error messages

    // Reset no results state
    setNoResults(false);
    setNonClothingError(false);
    setIsError(false);
};

  const handleKeyDown = (e) => {
    // trigger search on enter key
    if(e.key === "Enter" && searchText.trim() && !loading){
      e.preventDefault();
      e.stopPropagation();
      performOliSearch();
    }
  }

  useEffect(() => {
    handlePopupVisibility(isVisible);
    const handleOverlayClick = (e) => {
      if(popUpRef.current && !popUpRef.current.contains(e.target) && !e.target.closest(".popup")){
        popupCloseHandler();
      }
    };
    if(isVisible){
      // Use setTimeout to ensure the textarea is mounted before focusing
      setTimeout(() => {
        if (textareaRef) {
          textareaRef.current?.focus();
        }
      }, 100);
      document.addEventListener("mousedown", handleOverlayClick);
    }
    return () => {
      document.removeEventListener("mousedown", handleOverlayClick);
    }
  }, [isVisible]);

  const handleFocus = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsFocused(true);
    handlePopupVisibility(isVisible);
    if (isMobile) {
      const popup = document.querySelector('.popup');
      const inputField = document.querySelector('#oliPrompt');
      if (popup && inputField) {
        const offset = isPWA ? 200 : 110; //CONDITIONAL OFFSET ADDED TO CORRECT POPUP PLACEMENT ON FOCUS
        const inputFieldRect = inputField.getBoundingClientRect();
        // popup.style.top = `${inputFieldRect.top + window.scrollY - offset}px`;
        // popup.addEventListener('')
      }
    }
  };
  const handleBlur = () => {
    !searchText && setIsFocused(false);
    handlePopupVisibility(isVisible);
    if(isPWA || isMobile){
      const popup = document.querySelector('.popup');
      if(popup){
        popup.style.top = "";
      }
    }
  }

  // functions for Feedback Popup
  const closeFeedbackPopup = () => {
    dispatch(displayFeedbackPopup(false)); // Dispatch Redux action to hide feedback popup
    sessionStorage.setItem("showFeedbackPopup", "false"); // Update session storage
    dispatch(closeOliPopup());
  }

  const restoreFocusAfterError = () => {
    if(document?.activeElement === document?.body){
      const inputField = popUpRef.current?.querySelector("textarea");
      inputField?.focus();
    }
  };

  useEffect(() => {
    if (isError || noResults){
      restoreFocusAfterError();
    }
  }, [isError,noResults]);

  // 15 sec timer logic for feedback popup
  useEffect(() => {
    let timer;
    const shouldShowFeedback = sessionStorage.getItem("showFeedbackPopup") === "true"; //get the flag to display feedback popup from session storage
    if (isOliPLP && shouldShowFeedback){
      timer = setTimeout(() => {
        if(!isVisible){
          dispatch(displayFeedbackPopup(true));
        }
      },15000);
    }

    return () => clearTimeout(timer);
  }, [isOliPLP, isVisible]);

  // Retain page scroll position when the feedback popup is shown
  useEffect(() => {
    if(showFeedbackPopup){
      handlePopupVisibility(showFeedbackPopup, true);
      if(isMobile){
        document.body.classList.add("feedback_open");
      }
    }
    else{
      document.body.classList.remove("feedback_open");
    }
    return() => {
      document.body.classList.remove("feedback_open");
    }
  }, [showFeedbackPopup, isMobile]);

  const handleFeedback = async (feedbackType) => {
    setSelectedFeedback(feedbackType);

    const feedbackStatus = await analyzePromptFeedback(ga_session_id, feedbackType);
    console.log("Feedback Status: ", feedbackStatus);
    
    // setTimeout(() => {
      closeFeedbackPopup();
    // }, 1000);
  };

  return (
    <>
    {!isVisible && oliToggleStatus && (
      <button
        tabIndex={0}
        onClick={() => {fetchOliStatus(false); dispatch(openOliPopup());}}
        id="oli-popup"
        data-gaTrack={isPDP ? "ga-oli-pop-up-button-pdp": isDesigner? "ga-oli-pop-up-button-designer": isPLP ? "ga-oli-pop-up-button-plp" : isHomePage? "ga-oli-pop-up-button-home": ""}
        className={`gaTrack oli_popup_button ${styles.open_oliPopUp_button} ${isPDP ? "pdp_page" : ""} ${isPWA ? styles.app_position : ""}`}
        aria-label="Open Oli Pop Up"
      >
      <Oli_Icon animateOnLoad={!hasAnimated} onAnimationEnd={() => setHasAnimated(true)} height="30" width="30"
                className={`${isPDP ? "ga-oli-pop-up-button-pdp" : isDesigner? "ga-oli-pop-up-button-designer": isPLP ? "ga-oli-pop-up-button-plp" : isHomePage? "ga-oli-pop-up-button-home": ""}`}/>
      </button>)}

      {!isVisible && oliToggleStatus && (
        <OliPopUpMini
          firstName={firstName}
          isSubmenuVisible={isSubmenuVisible}
          isPDP={isPDP}
          isDesigner={isDesigner}
          isPLP={isPLP}
          isHomePage={isHomePage}
          fetchOliStatus={fetchOliStatus}
          dispatch={dispatch}
          openOliPopup={openOliPopup}
        />
      )}

      {isVisible && (
        <PopUp onClose={popupCloseHandler} show={isVisible} wrapperClass={`${styles.oli_popup_wrapper} ${isPWA ? styles.popup_position : ""}`}>
          <div className={styles.oli_popup} ref={popUpRef}>
          <Oli_Icon loading={loading} height="67" width="59"/>
          <h2 className={`x-large-md x-large-sm x-large-xs bold ${styles.popup_title}`}>
            {isError 
              ? nonClothingError 
                ? "Hmm—that's beyond my range right now" 
                : "Oops—something went wrong" 
              : loading 
              ? "Just a moment..." 
              : noResults 
              ? `Apologies,${firstName ? " " + firstName : ""}` 
              : `Hello${firstName ? " " + firstName + "," : ""}`
              }
            </h2>
            <p className={`normal-md normal-sm normal-xs ${styles.popup_description}`}>
            {
              isError
                ? nonClothingError
                  ? nonClothingError
                  : "Please try again."
                : loading
                ? "I'm searching Olivela's best to find the perfect picks for you."
                : noResults
                ? "I couldn't find an exact match, but let's try again by editing your search."
                : "Tell me what you're looking for, and I'll filter through our collection for you."
            }</p>
            <div className={styles.input_wrapper}>
            <textarea
              id="oliPrompt"
              name="oliPrompt"
              placeholder={isFocused? "" : "Red dress to wear to a wedding, under $2000, size 6"}
              aria-label="oli-prompt-input"
              className={`normal-md small-xs normal-sm  ${styles.input_field}`}
              tabIndex={0}
              value={searchText}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onFocus={(e)=>handleFocus(e)}
              onBlur={handleBlur}
              disabled={loading}
              rows="1"
              maxLength={inputMaxLength} // Add the maxLength attribute
              ref={textareaRef} // Attach the ref to the textarea
            />
            {!loading && !noResults && errorMessage && (
                <span className={`${styles.error_message} x-small-md x-small-sm x-small-xs error`}>
                  {errorMessage}
                </span>
              )}
            </div>
            <button tabIndex={0} onKeyDown={handleKeyDown} className={`gaTrack btn-primary ${styles.ask_ai_btn} ${isPDP ? "ga-oli-search-cta-pdp" : isDesigner? "ga-oli-search-cta-designer": isPLP ? "ga-oli-search-cta-plp" : isHomePage? "ga-oli-search-cta-home": ""}`} disabled={!searchText.trim() || loading} onClick={() => {
                  performOliSearch()
                }}>Ask Oli</button>
          </div>
        </PopUp>
      )}
      {showFeedbackPopup && (
        <PopUp onClose={closeFeedbackPopup} show={showFeedbackPopup} wrapperClass={`${styles.oli_popup_wrapper} ${styles.collapsed} ${isSubmenuVisible ? "d-none" : ""} oli-feedback-popup`} closeBtnClass="ga-oli-feedback-close">
        <div className={`${styles.oli_popup} ${selectedFeedback ? styles.popup_disabled : ""}`} ref={popUpRef}>
          <div className={styles.heading_wrapper}>
            <Oli_Icon height="47" width="43" className={styles.icon}/>
            <div className={styles.text_wrapper}>
              <h2 className={`"small-md small-sm small-xs bold ${styles.popup_title}`}>
              Find what you were looking for?
              </h2>
              <p className={`small-md small-sm small-xs ${styles.popup_description}`}>
                Let me know how I did.</p>
            </div>
          </div>  
            <div className={styles.feedback_wrapper}>
              <button tabIndex={0} id="oli-feedback" className={`${styles.feedback_button} ${styles.thumbs_up_button} ${selectedFeedback === "Positive" ? styles.active_button : styles.inactive_button}`} aria-label="Oli Feedback Thumbs Up" onClick={() => handleFeedback("Positive")}>
                <Srcset
                  requirePath={true}
                  svgFile={true}
                  src={"images/Icons/Oli_Icon/Thumbs_Up.svg"}
                  alt="Feed Back Thumbs UP"
                  height="60"
                  width="60"
                  className={`ga-oli-feedback-thumbs-up ${styles.feedback_icon_thumbs_up}`}
                />
                <span className={`small-md small-sm small-xs ga-oli-feedback-thumbs-up ${styles.feedback_text}`}>Yes&nbsp;I&nbsp;did</span>
              </button>
              <button tabIndex={0} id="oli-feedback" className={`${styles.feedback_button} ${selectedFeedback === "Negative" ? styles.active_button : styles.inactive_button}`} aria-label="Oli Feedback Thumbs Down" onClick={() => handleFeedback("Negative")}>
                <Srcset
                  requirePath={true}
                  svgFile={true}
                  src={"images/Icons/Oli_Icon/Thumbs_Down.svg"}
                  alt="Feed Back Thumbs Down"
                  height="60"
                  width="60"
                  className={`ga-oli-feedback-thumbs-down ${styles.feedback_icon_thumbs_down}`}
                />
                <span className={`small-md small-sm small-xs ga-oli-feedback-thumbs-down ${styles.feedback_text}`}>Not&nbsp;really</span>
              </button>
            </div>
        </div>
      </PopUp>
      )}
    </>
  );
}